import { Capacitor, registerPlugin } from '@capacitor/core';

import type { BackExcludedArea } from '../model/iosBackInteractions';

interface IosNavigationPlugin {
  interactions(options: {
    key: string;
    enabled: boolean;
    content: boolean;
    selected: boolean;
    valid: boolean;
    areas: BackExcludedArea[];
  }): Promise<void>;
  prepare(options: { currentKey: string; targetKey: string; targetHref: string }): Promise<void>;
  capture(options: { key: string }): Promise<void>;
  begin(options: {
    id: string;
    currentKey: string;
    targetKey: string;
    targetHref: string;
  }): Promise<void>;
  complete(options: { id: string; allow: boolean }): Promise<void>;
  rendered(options: { key: string; href: string }): Promise<void>;
  reset(): Promise<void>;
}

const plugin = registerPlugin<IosNavigationPlugin>('IosNavigation');

// Old TestFlight shells have no interactive bridge. Back remains available in those shells.
const safe = async (operation: () => Promise<void>) => {
  if (!Capacitor.isPluginAvailable('IosNavigation')) return;
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      operation(),
      new Promise<void>((resolve) => {
        timeout = setTimeout(resolve, 600);
      }),
    ]);
  } catch {
    // A missing snapshot must never block navigation or logout.
  } finally {
    clearTimeout(timeout);
  }
};

export const iosNavigation = {
  interactions: (options: Parameters<IosNavigationPlugin['interactions']>[0]) =>
    safe(() => plugin.interactions(options)),
  prepare: (options: Parameters<IosNavigationPlugin['prepare']>[0]) =>
    safe(() => plugin.prepare(options)),
  capture: (key: string) => safe(() => plugin.capture({ key })),
  begin: (options: Parameters<IosNavigationPlugin['begin']>[0]) =>
    safe(() => plugin.begin(options)),
  complete: (id: string, allow: boolean) => safe(() => plugin.complete({ id, allow })),
  rendered: (key: string, href: string) => safe(() => plugin.rendered({ key, href })),
  reset: () => safe(() => plugin.reset()),
  resetForIdentity: () => {
    window.dispatchEvent(new Event('teamplay:ios-navigation-identity-reset'));
    return safe(() => plugin.reset());
  },
};
